import React from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faOilCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, Paper, makeStyles, useMediaQuery, useTheme, Theme } from '@material-ui/core';
import Search from '@material-ui/icons/Search';
import SettingsOutlined from '@material-ui/icons/SettingsOutlined';
import { Button, LoadingBounce, Tab, TabBar, TabPanel, Tabs, usePhoneView } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { ModalSectionTitle } from 'components/ClosableDialog/ModalSectionTitle';
import { CustomParts } from 'compositions/Wizard/SelectTiresAndParts/CustomParts';
import { WizardAdditionalServices } from 'compositions/Wizard/SelectTiresAndParts/WizardAdditionalServices';
import WizardTireInformation from 'compositions/Wizard/SelectTiresAndParts/WizardTireInformation';
import { getVehicleLabel } from 'compositions/Wizard/SelectVehicle/VehicleSelection';
import { SelectedServicesType, SelectedTire } from 'compositions/Wizard/WizardContext';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { ReactComponent as Alignment } from 'icons/alignment.svg';
import { ReactComponent as Brakes } from 'icons/brakes.svg';
import { ReactComponent as Inspection } from 'icons/car_clipboard.svg';
import { ReactComponent as Tire } from 'icons/tires.svg';
import { ReactComponent as CarArrows } from 'icons/wheels_arrows.svg';
import { OrderType } from 'types/OrderType';
import { AdditionalServicesType, LocationCore, UFOPart, VehicleType } from 'types/UFOTypes';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { IconWithPqlNotification } from './IconWithPqlNotification';
import { useSelectTiresAndParts } from './useSelectTiresAndParts';

const useStyles = makeStyles((theme: Theme) => ({
  tabIcon: {
    width: '50px !important',
    height: '50px !important',
    marginBottom: '0px !important',
    transform: 'scale(0.65)',
  },
  lookupTabIcon: {
    width: '50px !important',
    height: '50px !important',
    transform: 'scale(0.65)',
    marginTop: '3px !important',
    marginBottom: '0px !important',
  },
  tabClass: {
    minWidth: 100,
    padding: 0,
    [theme.breakpoints.down('xs')]: {
      transform: 'scale(0.85)',
    },
  },
  gridClass: {
    margin: 0,
    padding: 0,
  },
}));
// TS identify some props as not being used, but they are used on useSelectTiresAndParts hook
export interface SelectTiresAndPartsProps {
  order?: OrderType;
  vehicle: VehicleType | null;
  setSelectedTires: React.Dispatch<React.SetStateAction<SelectedTire[]>>;
  fleet: LocationCore | null;
  provider: LocationCore | null;
  setSelectedServices: React.Dispatch<React.SetStateAction<SelectedServicesType[]>>;
  selectedTires: SelectedTire[];
  selectedServices: SelectedServicesType[];
  additionalServices: AdditionalServicesType[];
  loading: boolean;
  commonPricesParams: CommonPricesParamsType;
  isMobile: boolean;
}

export default function SelectTiresAndParts(props: SelectTiresAndPartsProps) {
  const { t } = useTranslation();
  const classes = useStyles();

  const theme = useTheme();
  const isDealer = useCurrentLocationIsDealer();

  const {
    vehicle,
    setSelectedTires,
    provider,
    setSelectedServices,
    selectedTires,
    selectedServices,
    loading,
    commonPricesParams,
    isMobile,
  } = props;

  const mobileView = usePhoneView();
  const showProductAutocompleteOutside = useMediaQuery(theme.breakpoints.down(1050));

  const {
    pricesLoading,
    refs,
    filteredParts,
    noProducts,
    filteredServices,
    setTabValue,
    vehicleIsEdited,
    emptyPqlPrices,
    tabStyle,
    tabValue,
    searchedPart,
    highlight,
    setCustomParts,
    fleetId,
    ProductAutocomplete,
    fittingTires,
  } = useSelectTiresAndParts(props);

  if (loading || pricesLoading) return <LoadingBounce style={{ height: '70vh' }} />;

  if (noProducts && vehicle) {
    return (
      <ModalSectionTitle
        title={t('There are no available products for {{vehicle}}', { vehicle: getVehicleLabel(vehicle) })}
      />
    );
  }
  if (!commonPricesParams) return null;

  return (
    <>
      {showProductAutocompleteOutside && vehicle && (
        <div style={{ margin: '0px 8px 8px' }}>
          <ProductAutocomplete />
        </div>
      )}

      <TabBar position="sticky">
        {!showProductAutocompleteOutside && (
          <div
            style={{
              position: 'absolute',
              right: 16,
              bottom: 16,
              width: 250,
              zIndex: 1,
            }}
          >
            <ProductAutocomplete />
          </div>
        )}
        <Tabs
          variant="scrollable"
          scrollButtons="on"
          value={tabValue}
          onChange={(event, newValue) => {
            setTabValue(newValue as number);
          }}
        >
          {vehicle && filteredParts.length > 0 && (
            <Tab
              label={t('Tires')}
              icon={<Tire fill={theme.palette.text.primary} className={classes.tabIcon} />}
              className={classes.tabClass}
              value={0}
            />
          )}
          {vehicle &&
            !vehicleIsEdited &&
            filteredServices.some((as) => as.additional_information?.category === 'FLUID') && (
              <Tab
                label={t('Fluids')}
                icon={
                  <IconWithPqlNotification
                    icon={<FontAwesomeIcon icon={faOilCan as IconProp} className={classes.tabIcon} />}
                    missingPql={emptyPqlPrices?.some((p) => p.category === 'FLUID')}
                  />
                }
                className={classes.tabClass}
                value={1}
              />
            )}
          {vehicle &&
            !vehicleIsEdited &&
            filteredServices.some((as) => as.additional_information?.category === 'BRAKES') && (
              <Tab
                label={t('Brakes')}
                icon={
                  <IconWithPqlNotification
                    icon={<Brakes fill={theme.palette.text.primary} style={{ margin: -8, transform: 'scale(0.7)' }} />}
                    missingPql={emptyPqlPrices?.some((p) => p.category === 'BRAKES')}
                  />
                }
                className={classes.tabClass}
                value={2}
              />
            )}
          {vehicle &&
            !vehicleIsEdited &&
            filteredServices.some((as) => as.additional_information?.category === 'ALIGNMENT') && (
              <Tab
                label={t('Alignment')}
                icon={
                  <IconWithPqlNotification
                    icon={<Alignment fill={theme.palette.text.primary} className={classes.tabIcon} />}
                    missingPql={emptyPqlPrices?.some((p) => p.category === 'ALIGNMENT')}
                  />
                }
                className={classes.tabClass}
                value={3}
              />
            )}
          {vehicle &&
            !vehicleIsEdited &&
            filteredServices.some((as) => as.additional_information?.category === 'INSPECTION') && (
              <Tab
                label={t('Inspection')}
                icon={
                  <IconWithPqlNotification
                    icon={
                      <Inspection
                        fill={theme.palette.text.primary}
                        style={{
                          margin: -28,
                          marginTop: -42,
                          paddingTop: 2,
                          transform: 'scale(0.7)',
                        }}
                      />
                    }
                    missingPql={emptyPqlPrices?.some((p) => p.category === 'INSPECTION')}
                  />
                }
                className={classes.tabClass}
                value={4}
              />
            )}
          {vehicle &&
            !vehicleIsEdited &&
            filteredServices.some((as) => as.additional_information?.category === 'TIRESERVICE') && (
              <Tab
                label={t('Tire Service')}
                icon={
                  <IconWithPqlNotification
                    icon={
                      <CarArrows
                        fill={theme.palette.text.primary}
                        style={{ margin: -20, marginTop: -24, transform: 'scale(0.7)' }}
                      />
                    }
                    missingPql={emptyPqlPrices?.some((p) => p.category === 'TIRESERVICE')}
                  />
                }
                className={classes.tabClass}
                value={5}
              />
            )}
          {vehicle &&
            !vehicleIsEdited &&
            filteredServices.some((as) => as.additional_information?.category === 'MISC') && (
              <Tab
                label={t('Misc')}
                icon={
                  <IconWithPqlNotification
                    icon={<SettingsOutlined className={classes.tabIcon} />}
                    missingPql={emptyPqlPrices?.some((p) => p.category === 'MISC')}
                  />
                }
                className={classes.tabClass}
                value={6}
              />
            )}
          {vehicle && !vehicleIsEdited && (
            <Tab
              label={t('Lookup')}
              icon={<Search className={classes.lookupTabIcon} />}
              className={classes.tabClass}
              value={7}
            />
          )}
          {!vehicle && (
            <Tab
              label={t('Tires')}
              icon={<Tire fill={theme.palette.text.primary} className={classes.tabIcon} />}
              className={classes.tabClass}
              value={90}
            />
          )}
          {!vehicle && (
            <Tab
              label={t('Lookup')}
              icon={<Search className={classes.lookupTabIcon} />}
              className={classes.tabClass}
              value={91}
            />
          )}
          {!vehicle && filteredServices.some((as) => as.additional_information?.category === 'MISC') && (
            <Tab
              label={t('Misc')}
              icon={<SettingsOutlined className={classes.tabIcon} style={{ marginTop: 8 }} />}
              className={classes.tabClass}
              value={92}
            />
          )}
        </Tabs>
      </TabBar>
      <TabPanel style={tabStyle} value={tabValue} index={0}>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          <Grid container spacing={0} className={classes.gridClass}>
            {filteredParts.map((tire: UFOPart) => (
              <>
                <Grid item xs={12} md={6} className={classes.gridClass}>
                  <li key={tire.mspn} ref={refs?.[tire.mspn]}>
                    <Paper
                      elevation={2}
                      style={{
                        padding: 16,
                        margin: 8,
                        background:
                          searchedPart?.mspn === getMspn(tire) && highlight ? 'rgba(177, 233, 233, 0.5)' : undefined,
                      }}
                      id="partPaper"
                    >
                      <WizardTireInformation
                        isLumped={!!tire?.child?.length}
                        tire={tire}
                        vehicle={vehicle || undefined}
                        selectedTires={selectedTires}
                        setSelectedTires={setSelectedTires}
                        commonPricesParams={commonPricesParams}
                        fittingTires={fittingTires}
                      />
                    </Paper>
                  </li>
                </Grid>
              </>
            ))}
            <Grid item xs={12} style={{ height: 200 }} />
          </Grid>
        </ul>
      </TabPanel>
      <TabPanel style={tabStyle} value={tabValue} index={1}>
        <WizardAdditionalServices
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          additionalServices={filteredServices}
          searchedPart={searchedPart}
          refs={refs}
          highlight={highlight}
          category="FLUID"
          commonPricesParams={commonPricesParams}
        />
      </TabPanel>
      <TabPanel style={tabStyle} value={tabValue} index={2}>
        <WizardAdditionalServices
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          additionalServices={filteredServices}
          searchedPart={searchedPart}
          refs={refs}
          highlight={highlight}
          category="BRAKES"
          commonPricesParams={commonPricesParams}
        />
      </TabPanel>
      <TabPanel style={tabStyle} value={tabValue} index={3}>
        <WizardAdditionalServices
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          additionalServices={filteredServices}
          searchedPart={searchedPart}
          refs={refs}
          highlight={highlight}
          category="ALIGNMENT"
          commonPricesParams={commonPricesParams}
        />
      </TabPanel>
      <TabPanel style={tabStyle} value={tabValue} index={4}>
        <WizardAdditionalServices
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          additionalServices={filteredServices}
          searchedPart={searchedPart}
          refs={refs}
          highlight={highlight}
          category="INSPECTION"
          commonPricesParams={commonPricesParams}
        />
      </TabPanel>
      <TabPanel style={tabStyle} value={tabValue} index={5}>
        <WizardAdditionalServices
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          additionalServices={filteredServices}
          searchedPart={searchedPart}
          refs={refs}
          highlight={highlight}
          category="TIRESERVICE"
          commonPricesParams={commonPricesParams}
        />
      </TabPanel>
      <TabPanel style={tabStyle} value={tabValue} index={6}>
        <WizardAdditionalServices
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          additionalServices={filteredServices}
          searchedPart={searchedPart}
          refs={refs}
          highlight={highlight}
          category="MISC"
          commonPricesParams={commonPricesParams}
        />
      </TabPanel>
      <TabPanel
        style={tabStyle}
        value={tabValue}
        index={7}
      >
        <CustomParts
          setPartsList={setCustomParts}
          searchedPart={getMspn(searchedPart)}
          setSelectedTires={setSelectedTires}
          fleet_id={fleetId}
          provider={provider}
          selectedTires={selectedTires}
          commonPricesParams={commonPricesParams}
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          vehicle={vehicle || undefined}
          fittingTires={fittingTires}
          isMobile={isMobile}
        />
      </TabPanel>
      <TabPanel
        style={tabStyle}
        value={tabValue}
        index={90}
      >
        <CustomParts
          setPartsList={setCustomParts}
          searchedPart={getMspn(searchedPart)}
          setSelectedTires={setSelectedTires}
          fleet_id={fleetId}
          provider={provider}
          selectedTires={selectedTires}
          commonPricesParams={commonPricesParams}
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          searchFor="size"
          vehicle={vehicle || undefined}
          fittingTires={fittingTires}
          isMobile={isMobile}
        />
      </TabPanel>
      <TabPanel
        style={tabStyle}
        value={tabValue}
        index={91}
      >
        <CustomParts
          setPartsList={setCustomParts}
          searchedPart={getMspn(searchedPart)}
          setSelectedTires={setSelectedTires}
          fleet_id={fleetId}
          provider={provider}
          selectedTires={selectedTires}
          commonPricesParams={commonPricesParams}
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          searchFor="mspn"
          vehicle={vehicle || undefined}
          fittingTires={fittingTires}
          isMobile={isMobile}
        />
      </TabPanel>
      <TabPanel style={{ background: theme.palette.background.default }} value={tabValue} index={92}>
        <WizardAdditionalServices
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          additionalServices={filteredServices}
          searchedPart={searchedPart}
          refs={refs}
          highlight={highlight}
          category="MISC"
          commonPricesParams={commonPricesParams}
        />
      </TabPanel>
      {!isDealer && (tabValue === 0 || tabValue === 7) && (
        <Button
          onClick={() =>
            window.open(
              'https://www.michelinman.com/auto/tips-and-advice/advice-auto/tire-buying-guide/find-your-tire-size',
            )
          }
          size="small"
          variant="contained"
          color="primary"
          style={{
            position: 'fixed',
            bottom: mobileView ? 0 : 4,
            marginBottom: 48,
            marginLeft: 8,
            transform: mobileView ? 'scale(0.8)' : 'scale(1)',
            transformOrigin: 'center left',
          }}
        >
          {t('Find Your Tire Size')}
        </Button>
      )}
    </>
  );
}
